import type { QueryClient } from '@tanstack/react-query'
import { decksQueries } from '@/hooks/decks/useDecks'
import { SessionChangedError } from '@/services/api/SessionChangedError'

// ---------------------------------------------------------------------------
// Invalidation groups — cross-cutting concern
// ---------------------------------------------------------------------------

export const INVALIDATION_GROUPS = {
  collection: [['collection'], ['collectionQuantitiesByBase'], ['dashboardStats'], ['userQuantities']],
  buylist: [['buylist'], ['userQuantities']],
  selllist: [['selllist'], ['userQuantities']],
  decks: [['decks'], ['deck'], ['deckStats'], ['deckCollectionStatus']],
  sealedCollection: [['sealedCollection'], ['dashboardStats'], ['userQuantities']],
  subscriptions: [['subscriptions']],
  notifications: [['notifications']],
  userFilters: [['userFilters'], ['cards'], ['setRarities'], ['sealedProducts']],
  adminJobs: [['admin', 'jobs']],
  adminSessions: [['admin', 'sessions']],
  adminFeatures: [['admin', 'features'], ['features']],
} as const

export type InvalidationGroup = keyof typeof INVALIDATION_GROUPS

// ---------------------------------------------------------------------------
// Invalidation helpers
// ---------------------------------------------------------------------------

export function invalidateGroup(queryClient: QueryClient, group: InvalidationGroup) {
  const keys = INVALIDATION_GROUPS[group]
  keys.forEach((key) => {
    queryClient.invalidateQueries({ queryKey: key })
  })
}

function invalidateGroups(queryClient: QueryClient, groups: InvalidationGroup[]) {
  groups.forEach((group) => invalidateGroup(queryClient, group))
}

/** Invalidate a specific list of query keys. */
function invalidateKeys(queryClient: QueryClient, keys: readonly (readonly unknown[])[]) {
  keys.forEach((key) => {
    queryClient.invalidateQueries({ queryKey: key })
  })
}

// --- Optimistic update helpers for quantity maps ---

type QuantityMap = Record<number, number>
type RollbackCtx = { prev?: QuantityMap; prevDrain?: QuantityMap }

/** Optimistic add/increment for a quantity map cache.
 *
 * Pass `opts.drain` to also optimistically drain a second quantity-map cache by
 * the same quantity (floor at 0; delete the entry once it would reach 0).
 * Used by the card-collection add to keep the buylist count in sync (#101).
 * The drain cache is restored alongside the primary cache on error. */
export function optimisticAdd<TVar>(
  queryClient: QueryClient,
  quantitiesKey: readonly unknown[],
  settledInvalidation: readonly (readonly unknown[])[],
  getId: (vars: TVar) => number,
  getQuantity: (vars: TVar) => number = () => 1,
  opts?: { drain?: { quantitiesKey: readonly unknown[] } },
) {
  const drainKey = opts?.drain?.quantitiesKey
  return {
    onMutate: async (vars: TVar) => {
      const id = getId(vars)
      const qty = getQuantity(vars)

      await queryClient.cancelQueries({ queryKey: quantitiesKey })
      const prev = queryClient.getQueryData<QuantityMap>(quantitiesKey)
      if (prev) {
        queryClient.setQueryData(quantitiesKey, { ...prev, [id]: (prev[id] ?? 0) + qty })
      }

      let prevDrain: QuantityMap | undefined
      if (drainKey) {
        await queryClient.cancelQueries({ queryKey: drainKey })
        prevDrain = queryClient.getQueryData<QuantityMap>(drainKey)
        if (prevDrain) {
          const current = prevDrain[id] ?? 0
          if (current > 0) {
            const next = { ...prevDrain }
            if (qty >= current) delete next[id]
            else next[id] = current - qty
            queryClient.setQueryData(drainKey, next)
          }
        }
      }

      return { prev, prevDrain }
    },
    onError: (err: unknown, _vars: TVar, ctx: RollbackCtx | undefined) => {
      if (err instanceof SessionChangedError) return
      if (ctx?.prev) queryClient.setQueryData(quantitiesKey, ctx.prev)
      if (ctx?.prevDrain && drainKey) queryClient.setQueryData(drainKey, ctx.prevDrain)
    },
    onSettled: () => invalidateKeys(queryClient, settledInvalidation),
  }
}

/** Optimistic set/replace for a quantity map cache. */
export function optimisticSet<TVar>(
  queryClient: QueryClient,
  quantitiesKey: readonly unknown[],
  settledInvalidation: readonly (readonly unknown[])[],
  getId: (vars: TVar) => number,
  getQuantity: (vars: TVar) => number | undefined,
) {
  return {
    onMutate: async (vars: TVar): Promise<RollbackCtx> => {
      const qty = getQuantity(vars)
      if (qty == null) return { prev: undefined }
      await queryClient.cancelQueries({ queryKey: quantitiesKey })
      const prev = queryClient.getQueryData<QuantityMap>(quantitiesKey)
      if (prev) {
        queryClient.setQueryData(quantitiesKey, {
          ...prev,
          [getId(vars)]: qty,
        })
      }
      return { prev }
    },
    onError: (err: unknown, _vars: TVar, ctx: RollbackCtx | undefined) => {
      if (err instanceof SessionChangedError) return
      if (ctx?.prev) {
        queryClient.setQueryData(quantitiesKey, ctx.prev)
      }
    },
    onSettled: () => invalidateKeys(queryClient, settledInvalidation),
  }
}

/** Optimistic remove/delete-key for a quantity map cache.
 *
 * Pass `opts.getAll` to enable decrement-vs-delete mode: when the callback
 * returns true (or the current quantity is ≤ 1) the entry is deleted; otherwise
 * it is decremented by 1.  Without `opts` the entry is always deleted, which
 * preserves the original behaviour for collection removes. */
export function optimisticRemove<TVar>(
  queryClient: QueryClient,
  quantitiesKey: readonly unknown[],
  settledInvalidation: readonly (readonly unknown[])[],
  getId: (vars: TVar) => number,
  opts?: { getAll?: (vars: TVar) => boolean },
) {
  return {
    onMutate: async (vars: TVar) => {
      await queryClient.cancelQueries({ queryKey: quantitiesKey })
      const prev = queryClient.getQueryData<QuantityMap>(quantitiesKey)
      if (prev) {
        const id = getId(vars)
        if (opts?.getAll !== undefined) {
          const currentQty = prev[id] ?? 0
          if (opts.getAll(vars) || currentQty <= 1) {
            const next = { ...prev }
            delete next[id]
            queryClient.setQueryData(quantitiesKey, next)
          } else {
            queryClient.setQueryData(quantitiesKey, { ...prev, [id]: currentQty - 1 })
          }
        } else {
          const next = { ...prev }
          delete next[id]
          queryClient.setQueryData(quantitiesKey, next)
        }
      }
      return { prev }
    },
    onError: (err: unknown, _vars: TVar, ctx: RollbackCtx | undefined) => {
      if (err instanceof SessionChangedError) return
      if (ctx?.prev) {
        queryClient.setQueryData(quantitiesKey, ctx.prev)
      }
    },
    onSettled: () => invalidateKeys(queryClient, settledInvalidation),
  }
}

/** Returns { onSettled } that invalidates either a group or specific keys. */
export function onSettledInvalidate(
  queryClient: QueryClient,
  groupOrKeys: InvalidationGroup | readonly (readonly unknown[])[],
) {
  return {
    onSettled: () => {
      if (typeof groupOrKeys === 'string') {
        invalidateGroup(queryClient, groupOrKeys)
      } else {
        invalidateKeys(queryClient, groupOrKeys)
      }
    },
  }
}

/** Returns { onSuccess } that invalidates either a group or specific keys. */
export function onSuccessInvalidate(
  queryClient: QueryClient,
  groupOrKeys: InvalidationGroup | readonly (readonly unknown[])[],
) {
  return {
    onSuccess: () => {
      if (typeof groupOrKeys === 'string') {
        invalidateGroup(queryClient, groupOrKeys)
      } else {
        invalidateKeys(queryClient, groupOrKeys)
      }
    },
  }
}

/** Invalidates deck detail, stats, and collection status for a given deck. */
export function invalidateDeckDetail(queryClient: QueryClient, deckId: number) {
  queryClient.invalidateQueries({ queryKey: decksQueries.detail.queryKey(deckId) })
  queryClient.invalidateQueries({ queryKey: decksQueries.stats.queryKey(deckId) })
  queryClient.invalidateQueries({ queryKey: decksQueries.collectionStatus.queryKey(deckId) })
}

/** Invalidates every defined invalidation group. */
export function invalidateAll(queryClient: QueryClient) {
  const groups = Object.keys(INVALIDATION_GROUPS) as InvalidationGroup[]
  invalidateGroups(queryClient, groups)
}
